import * as React from 'react';
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Collapse,
  Paper,
  Divider,
  Badge,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { fi } from 'date-fns/locale';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import FilterListIcon from '@mui/icons-material/FilterList';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

// Stored filter dates are always "yyyy-MM-dd". Anything else parses to an Invalid Date, and the
// list views call toISOString() on what getDateRange hands back, which throws a RangeError and
// takes the whole route down. Filters live in localStorage, so one bad value used to crash the
// page on every later visit too. Validate before parsing, everywhere.
const DATE_STRING_RE = /^\d{4}-\d{2}-\d{2}$/;

export const isValidDateString = (value) =>
  typeof value === 'string' &&
  DATE_STRING_RE.test(value) &&
  !isNaN(new Date(`${value}T00:00:00`).getTime());

// Read a stored filter date. A value saved by an older build that cannot be parsed back is
// treated as no date at all, so the pickers come up empty and the summary chip stops claiming
// a filter that is not being applied.
export const sanitizeDateString = (value) => (isValidDateString(value) ? value : '');

// Helper to convert "yyyy-MM-dd" string to Date object
export const dateStringToDate = (dateStr) => {
  if (!isValidDateString(dateStr)) return null;
  return new Date(dateStr + 'T00:00:00');
};

// Helper to convert Date object to "yyyy-MM-dd" string
export const dateToDateString = (date) => {
  if (!date || isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  // The pickers publish a value after every keystroke, so a year typed as 26 (or half-typed as
  // 2) arrives here as a perfectly valid Date in year 26. Storing it would yield "26-09-13",
  // which JS cannot parse back. Drop it rather than write a string nothing can read.
  if (year < 1900 || year > 2100) return '';
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// ==================== HOOKS ====================

/**
 * Hook for managing list preferences with localStorage persistence
 * @param {string} storageKey - Key for localStorage
 * @param {object} defaultPrefs - Default preferences object
 */
export const useListPreferences = (storageKey, defaultPrefs) => {
  const loadPreferences = useCallback(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          ...defaultPrefs,
          ...parsed,
          filters: {
            ...defaultPrefs.filters,
            ...(parsed.filters || {}),
          },
        };
      }
    } catch (e) {
      console.error('Failed to load preferences:', e);
    }
    return defaultPrefs;
  }, [storageKey, defaultPrefs]);

  const [preferences, setPreferences] = useState(loadPreferences);

  const savePreferences = useCallback((prefs) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(prefs));
    } catch (e) {
      console.error('Failed to save preferences:', e);
    }
  }, [storageKey]);

  const updatePreferences = useCallback((updates) => {
    setPreferences((prev) => {
      const newPrefs = typeof updates === 'function' ? updates(prev) : { ...prev, ...updates };
      savePreferences(newPrefs);
      return newPrefs;
    });
  }, [savePreferences]);

  const updateFilters = useCallback((filterUpdates) => {
    updatePreferences((prev) => ({
      ...prev,
      filters: { ...prev.filters, ...filterUpdates },
    }));
  }, [updatePreferences]);

  const resetFilters = useCallback(() => {
    updatePreferences({
      ...defaultPrefs,
      sortField: preferences.sortField,
      sortDirection: preferences.sortDirection,
    });
  }, [updatePreferences, defaultPrefs, preferences.sortField, preferences.sortDirection]);

  const handleSort = useCallback((field) => {
    updatePreferences((prev) => ({
      ...prev,
      sortField: field,
      sortDirection: prev.sortField === field && prev.sortDirection === 'asc' ? 'desc' : 'asc',
    }));
  }, [updatePreferences]);

  // filtersOpen from preferences (with fallback for existing data)
  const filtersOpen = preferences.filtersOpen ?? false;

  const setFiltersOpen = useCallback((open) => {
    updatePreferences({ filtersOpen: open });
  }, [updatePreferences]);

  const toggleFiltersOpen = useCallback(() => {
    updatePreferences((prev) => ({ ...prev, filtersOpen: !prev.filtersOpen }));
  }, [updatePreferences]);

  return {
    preferences,
    updatePreferences,
    updateFilters,
    resetFilters,
    handleSort,
    filtersOpen,
    setFiltersOpen,
    toggleFiltersOpen,
  };
};

// ==================== COMPONENTS ====================

/**
 * Sortable table header component
 */
export const SortableHeader = ({ label, field, currentSort, currentDirection, onSort, align = 'left', sx = {} }) => {
  const isActive = currentSort === field;

  return (
    <Box
      onClick={() => onSort(field)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        cursor: 'pointer',
        userSelect: 'none',
        justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
        '&:hover': {
          '& .MuiTypography-root': {
            color: 'text.primary',
          },
        },
        ...sx,
      }}
    >
      <Typography
        sx={{
          fontSize: '12px',
          fontWeight: 600,
          color: isActive ? 'secondary.main' : 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          transition: 'color 0.15s ease',
        }}
      >
        {label}
      </Typography>
      {isActive && (
        currentDirection === 'asc' ? (
          <ArrowUpwardIcon sx={{ fontSize: 14, color: 'secondary.main' }} />
        ) : (
          <ArrowDownwardIcon sx={{ fontSize: 14, color: 'secondary.main' }} />
        )
      )}
    </Box>
  );
};

/**
 * Filter chip toggle button
 */
export const FilterChip = ({ icon, label, active, onClick, color = 'default', 'data-testid': dataTestId }) => (
  <Chip
    icon={icon}
    label={label}
    onClick={onClick}
    data-testid={dataTestId}
    variant={active ? 'filled' : 'outlined'}
    color={active ? color : 'default'}
    sx={{
      borderRadius: '8px',
      fontWeight: active ? 500 : 400,
      '& .MuiChip-icon': {
        fontSize: 18,
      },
    }}
  />
);

/**
 * Filter button with badge showing active filter count
 */
export const FilterButton = ({ t, activeCount, isOpen, onClick }) => (
  <Badge
    badgeContent={activeCount}
    color="secondary"
    sx={{
      '& .MuiBadge-badge': {
        right: 12,
        top: 8,
      },
    }}
  >
    <Button
      variant={isOpen || activeCount > 0 ? 'contained' : 'outlined'}
      startIcon={<FilterListIcon />}
      onClick={onClick}
      sx={{
        px: 2,
        borderRadius: '10px',
        borderColor: isOpen || activeCount > 0 ? 'secondary.main' : 'divider',
        bgcolor: isOpen || activeCount > 0 ? 'secondary.main' : 'transparent',
        color: isOpen || activeCount > 0 ? 'white' : 'text.primary',
        fontWeight: 500,
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        whiteSpace: 'nowrap',
        minWidth: { xs: '100%', sm: 'auto' },
        '&:hover': {
          borderColor: 'secondary.main',
          bgcolor: isOpen || activeCount > 0 ? 'secondary.light' : 'background.paper',
        },
      }}
    >
      {t('Filter')}
    </Button>
  </Badge>
);

/**
 * Search input with clear button and built-in debouncing
 * Internal state keeps typing smooth while onChange is debounced
 */
export const SearchInput = ({ value, onChange, onClear = () => {}, onEnter, placeholder, maxWidth, debounceMs = 300, autoFocus = false, 'data-testid': dataTestId }) => {
  const [inputValue, setInputValue] = useState(value || '');
  const debounceRef = useRef(null);
  const isExternalUpdate = useRef(false);

  // Sync internal state when external value changes (e.g., on clear or reset)
  useEffect(() => {
    if (value !== inputValue && !isExternalUpdate.current) {
      setInputValue(value || '');
    }
    isExternalUpdate.current = false;
  }, [value]);

  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // Clear existing debounce timer
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce the onChange callback
    debounceRef.current = setTimeout(() => {
      isExternalUpdate.current = true;
      onChange(newValue);
    }, debounceMs);
  }, [onChange, debounceMs]);

  const handleClear = useCallback(() => {
    setInputValue('');
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    onClear();
  }, [onClear]);

  // Optional Enter handling (used by the calendar search). The debounced onChange may not
  // have fired yet, so flush it and hand the caller the value actually in the field.
  const handleKeyDown = useCallback((e) => {
    if (e.key !== 'Enter' || !onEnter) return;
    e.preventDefault();
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    isExternalUpdate.current = true;
    onChange(inputValue);
    onEnter(inputValue);
  }, [onEnter, onChange, inputValue]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return (
    <TextField
      placeholder={placeholder}
      value={inputValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      autoFocus={autoFocus}
      fullWidth
      data-testid={dataTestId}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ color: 'text.disabled' }} />
          </InputAdornment>
        ),
        endAdornment: inputValue && (
          <InputAdornment position="end">
            <IconButton
              size="small"
              onClick={handleClear}
              sx={{ color: 'text.disabled' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ),
      }}
      sx={{
        maxWidth: maxWidth || { xs: '100%', md: 500 },
        '& .MuiOutlinedInput-root': {
          bgcolor: 'background.paper',
          borderRadius: '10px',
          '& fieldset': {
            borderColor: 'divider',
          },
        },
      }}
    />
  );
};

/**
 * Filter panel container with sections
 */
export const FilterPanel = ({
  open,
  children,
  resultCount,
  onReset,
  resetDisabled,
  t
}) => (
  <Collapse in={open}>
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 2.5,
        borderRadius: '12px',
        border: 1,
        borderColor: 'divider',
      }}
    >
      {children}

      {/* Actions Row */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {t('Showing {{count}} items', { count: resultCount })}
        </Typography>
        <Button
          size="small"
          startIcon={<RestartAltIcon />}
          onClick={onReset}
          disabled={resetDisabled}
          sx={{ color: 'text.secondary' }}
        >
          {t('Reset filters')}
        </Button>
      </Box>
    </Paper>
  </Collapse>
);

/**
 * Filter section with title
 */
export const FilterSection = ({ title, children, dividerTop = false, dividerBottom = false }) => (
  <>
    {dividerTop && <Divider sx={{ my: 2 }} />}
    <Box sx={{ mb: dividerBottom ? 0 : 3 }}>
      {title && (
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: 600,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            mb: 1.5,
          }}
        >
          {title}
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        {children}
      </Box>
    </Box>
    {dividerBottom && <Divider sx={{ my: 2 }} />}
  </>
);

/**
 * Multi-select dropdown filter
 */
export const MultiSelectFilter = ({ label, value, onChange, options, renderValue }) => (
  <FormControl size="small" sx={{ minWidth: 200 }}>
    <InputLabel>{label}</InputLabel>
    <Select
      multiple
      value={value}
      onChange={(e) => onChange(e.target.value)}
      label={label}
      renderValue={renderValue || ((selected) => selected.join(', '))}
    >
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          <Checkbox checked={value.includes(option.value)} />
          <ListItemText primary={option.label} />
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

/**
 * Date range filters
 */
export const DateRangeFilter = ({
  dateRangeType,
  dateFrom,
  dateTo,
  onTypeChange,
  onFromChange,
  onToChange,
  t
}) => (
  <>
    <FilterChip
      label={t('Today')}
      active={dateRangeType === 'today'}
      onClick={() => onTypeChange(dateRangeType === 'today' ? 'all' : 'today')}
      color="primary"
    />
    <FilterChip
      label={t('This week')}
      active={dateRangeType === 'this_week'}
      onClick={() => onTypeChange(dateRangeType === 'this_week' ? 'all' : 'this_week')}
      color="primary"
    />
    <FilterChip
      label={t('This month')}
      active={dateRangeType === 'this_month'}
      onClick={() => onTypeChange(dateRangeType === 'this_month' ? 'all' : 'this_month')}
      color="primary"
    />

    <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
      <DatePicker
        label={t('From')}
        value={dateStringToDate(dateFrom)}
        onChange={(newValue) => onFromChange(dateToDateString(newValue))}
        format="dd.MM.yyyy"
        slotProps={{
          textField: {
            size: 'small',
            sx: { width: 160 },
          },
        }}
      />
      <DatePicker
        label={t('To')}
        value={dateStringToDate(dateTo)}
        onChange={(newValue) => onToChange(dateToDateString(newValue))}
        format="dd.MM.yyyy"
        slotProps={{
          textField: {
            size: 'small',
            sx: { width: 160 },
          },
        }}
      />
    </LocalizationProvider>
  </>
);

/**
 * Get date range based on type
 */
export const getDateRange = (rangeType, dateFrom, dateTo) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  switch (rangeType) {
    case 'today':
      return { from: today, to: new Date(today.getTime() + 24 * 60 * 60 * 1000) };
    case 'this_week': {
      const startOfWeek = new Date(today);
      const day = startOfWeek.getDay();
      const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
      startOfWeek.setDate(diff);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(endOfWeek.getDate() + 7);
      return { from: startOfWeek, to: endOfWeek };
    }
    case 'this_month': {
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      return { from: startOfMonth, to: endOfMonth };
    }
    case 'custom':
      // A stored value that is not a real yyyy-MM-dd is dropped, not parsed: the callers feed
      // the result straight to toISOString(), and an Invalid Date there crashes the route.
      return {
        from: isValidDateString(dateFrom) ? new Date(dateFrom) : null,
        to: isValidDateString(dateTo) ? new Date(dateTo + 'T23:59:59') : null,
      };
    default:
      return { from: null, to: null };
  }
};

/**
 * Vertical divider for filter sections
 */
export const FilterDivider = () => (
  <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
);

/**
 * Filter summary showing active filters as chips
 * @param {array} chips - Array of { key: string, label: string }
 * @param {function} onClear - Callback to clear all filters
 * @param {function} t - Translation function
 */
export const FilterSummary = ({ chips, onClear, t }) => {
  if (!chips || chips.length === 0) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 2,
        flexWrap: 'wrap',
      }}
    >
      <Typography
        sx={{
          fontSize: '13px',
          color: 'text.secondary',
          fontWeight: 500,
        }}
      >
        {t('Filtered')}:
      </Typography>
      {chips.map((chip) => (
        <Chip
          key={chip.key}
          label={chip.label}
          size="small"
          variant="outlined"
          sx={{
            borderRadius: '6px',
            fontSize: '12px',
            height: 24,
            bgcolor: 'background.paper',
            borderColor: 'divider',
          }}
        />
      ))}
      <Button
        size="small"
        onClick={onClear}
        sx={{
          fontSize: '12px',
          color: 'text.secondary',
          minWidth: 'auto',
          px: 1,
          '&:hover': {
            color: 'secondary.main',
          },
        }}
      >
        {t('Clear')}
      </Button>
    </Box>
  );
};
